import { db } from "@/lib/firebase";
import {
  ScheduledJobService,
  type ScheduledJobInput,
} from "@/lib/scheduledJobService";
import {
  safeParseScheduledJob,
  safeParseScheduledJobRun,
  type ScheduledJob,
  type ScheduledJobRun,
} from "@/schemas/firestore/scheduledJob.schema";
import { captureException } from "@sentry/react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

export interface ScheduledJobsSlice {
  scheduledJobs: ScheduledJob[];
  /** Keyed by job_id. Only jobs whose history has been opened are present. */
  scheduledJobRuns: Record<string, ScheduledJobRun[]>;
  isScheduledJobsLoading: boolean;
  scheduledJobsError: string | null;

  /**
   * Live-read the domain's scheduled jobs.
   *
   * Direct-from-Firestore per ADR-0003 — the rule added in the `firestore` repo
   * allows `isOrgMember` reads here. The snapshot is also what makes
   * `next_run_at` advancing after a dispatch show up without a poll.
   */
  subscribeToScheduledJobs: (orgId: string, domainId: string) => () => void;
  /** Per job, opened on demand: a run history nobody is looking at is a listener nobody needs. */
  subscribeToScheduledJobRuns: (
    orgId: string,
    domainId: string,
    jobId: string,
  ) => () => void;

  createScheduledJob: (
    orgId: string,
    domainId: string,
    input: ScheduledJobInput,
  ) => Promise<void>;
  updateScheduledJob: (
    orgId: string,
    domainId: string,
    jobId: string,
    input: ScheduledJobInput,
  ) => Promise<void>;
  deleteScheduledJob: (
    orgId: string,
    domainId: string,
    jobId: string,
  ) => Promise<void>;
  runScheduledJobNow: (
    orgId: string,
    domainId: string,
    jobId: string,
  ) => Promise<void>;

  clearScheduledJobsError: () => void;
  unsubscribeScheduledJobs: () => void;

  _scheduledJobSubscriptions: Map<string, () => void>;
}

const messageFor = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

export const createScheduledJobsSlice: StateCreator<
  StoreState,
  [],
  [],
  ScheduledJobsSlice
> = (set, get) => ({
  scheduledJobs: [],
  scheduledJobRuns: {},
  isScheduledJobsLoading: false,
  scheduledJobsError: null,
  _scheduledJobSubscriptions: new Map(),

  subscribeToScheduledJobs: (orgId: string, domainId: string) => {
    if (!orgId || !domainId) return () => {};

    const key = `jobs:${orgId}:${domainId}`;
    const existing = get()._scheduledJobSubscriptions.get(key);
    if (existing) return existing;

    set((state) => ({
      isScheduledJobsLoading: state.scheduledJobs.length === 0,
      scheduledJobsError: null,
    }));

    const jobsQuery = query(
      collection(db, `organizations/${orgId}/domains/${domainId}/jobs`),
      orderBy("created_at", "desc"),
    );

    const unsubscribe = onSnapshot(
      jobsQuery,
      (snapshot) => {
        const jobs: ScheduledJob[] = [];
        snapshot.docs.forEach((docSnapshot) => {
          const parsed = safeParseScheduledJob({
            ...docSnapshot.data(),
            job_id: docSnapshot.id,
          });
          if (parsed.success) {
            jobs.push(parsed.data);
          } else {
            console.error(
              `Invalid scheduled job ${docSnapshot.id}`,
              parsed.error,
            );
            captureException(parsed.error, {
              extra: { jobId: docSnapshot.id },
            });
          }
        });
        set({ scheduledJobs: jobs, isScheduledJobsLoading: false });
      },
      (error) => {
        console.error("Scheduled jobs subscription failed", error);
        set({
          scheduledJobsError: error.message,
          isScheduledJobsLoading: false,
        });
      },
    );

    get()._scheduledJobSubscriptions.set(key, unsubscribe);
    return unsubscribe;
  },

  subscribeToScheduledJobRuns: (
    orgId: string,
    domainId: string,
    jobId: string,
  ) => {
    if (!orgId || !domainId || !jobId) return () => {};

    const key = `runs:${orgId}:${domainId}:${jobId}`;
    const existing = get()._scheduledJobSubscriptions.get(key);
    if (existing) return existing;

    const runsQuery = query(
      collection(
        db,
        `organizations/${orgId}/domains/${domainId}/jobs/${jobId}/runs`,
      ),
      orderBy("started_at", "desc"),
    );

    const unsubscribe = onSnapshot(
      runsQuery,
      (snapshot) => {
        const runs: ScheduledJobRun[] = [];
        snapshot.docs.forEach((docSnapshot) => {
          const parsed = safeParseScheduledJobRun({
            ...docSnapshot.data(),
            run_id: docSnapshot.id,
          });
          if (parsed.success) {
            runs.push(parsed.data);
          } else {
            console.error(`Invalid job run ${docSnapshot.id}`, parsed.error);
            captureException(parsed.error, {
              extra: { jobId, runId: docSnapshot.id },
            });
          }
        });
        set((state) => ({
          scheduledJobRuns: { ...state.scheduledJobRuns, [jobId]: runs },
        }));
      },
      (error) => {
        console.error("Job runs subscription failed", error);
        set({ scheduledJobsError: error.message });
      },
    );

    get()._scheduledJobSubscriptions.set(key, unsubscribe);
    return unsubscribe;
  },

  createScheduledJob: async (orgId, domainId, input) => {
    set({ scheduledJobsError: null });
    try {
      await ScheduledJobService.create(orgId, domainId, input);
    } catch (error) {
      set({ scheduledJobsError: messageFor(error, "Saving the job failed.") });
      captureException(error);
      throw error;
    }
  },

  updateScheduledJob: async (orgId, domainId, jobId, input) => {
    set({ scheduledJobsError: null });
    try {
      await ScheduledJobService.update(orgId, domainId, jobId, input);
    } catch (error) {
      set({ scheduledJobsError: messageFor(error, "Saving the job failed.") });
      captureException(error);
      throw error;
    }
  },

  deleteScheduledJob: async (orgId, domainId, jobId) => {
    set({ scheduledJobsError: null });
    try {
      await ScheduledJobService.remove(orgId, domainId, jobId);
    } catch (error) {
      set({ scheduledJobsError: messageFor(error, "Deleting the job failed.") });
      captureException(error);
    }
  },

  runScheduledJobNow: async (orgId, domainId, jobId) => {
    set({ scheduledJobsError: null });
    try {
      await ScheduledJobService.runNow(orgId, domainId, jobId);
    } catch (error) {
      set({ scheduledJobsError: messageFor(error, "Starting the run failed.") });
      captureException(error);
    }
  },

  clearScheduledJobsError: () => set({ scheduledJobsError: null }),

  unsubscribeScheduledJobs: () => {
    get()._scheduledJobSubscriptions.forEach((unsubscribe) => unsubscribe());
    get()._scheduledJobSubscriptions.clear();
    set({
      scheduledJobs: [],
      scheduledJobRuns: {},
      isScheduledJobsLoading: false,
      scheduledJobsError: null,
    });
  },
});
